import { t } from 'i18next';
import { isEqual } from 'lodash';
import { Outlet } from 'react-router-dom';
import { Breadcrumb } from '../../breadcrumbs/models/Breadcrumb';
import { Project as ProjectBreadcrumb } from '../../breadcrumbs/models/Project';
import { BaseController, BaseState } from '../../common/controllers/BaseController';
import { WithRouterProps, withRouter } from '../../common/higher-order-components/with-router';
import { TestValueWrapper } from '../../common/utils/TestValueWrapper';
import { UrlHelper } from '../../common/utils/UrlHelper';
import { bind } from '../../common/utils/decorators';
import { ProjectExportDrawer } from '../../exports/triggers/drawers/ProjectExportDrawer';
import { ProjectExportState } from '../../exports/triggers/states/ProjectExportState';
import { SecondaryNav } from '../../layout/components/SecondaryNav';
import { PROJECT_TABS, Route } from '../../routing/routes';
import { ProjectBreadcrumbMapper } from '../common/mappers/ProjectBreadcrumbMapper';
import { Project } from '../common/models/Project';
import { ProjectRepository } from '../common/services/ProjectRepository';

type ProjectNavParams = { projectId: string };

type ProjectNavHeader = string | undefined;

type Props = WithRouterProps & {};

type State = BaseState & {
  isLoadingNavContext: boolean;
};

class Controller extends BaseController<Props, State> {
  private readonly projectRepository = new ProjectRepository();
  private readonly projectBreadcrumbMapper = new ProjectBreadcrumbMapper();

  private projectId: number;
  private project: Project | null = null;

  private navHeader: ProjectNavHeader | undefined;
  private nestedTabs: Route[] | undefined;

  private projectBreadcrumb: ProjectBreadcrumb | null | undefined;

  private exportState: ProjectExportState;

  constructor(props: Props, context: unknown) {
    super(props, context);

    ({ projectId: this.projectId } = this.parseNumericIds(['projectId']));
    this.updateProjectBreadcrumb();

    this.exportState = ProjectExportState.getInstance();

    this.state = {
      ...this.state,
      isLoadingNavContext: !this.projectBreadcrumb,
    };
  }

  componentDidMount() {
    super.componentDidMount();

    this.getData();
  }

  componentWillUnmount() {
    super.componentWillUnmount();

    this.exportState.reset();
    this.projectRepository.cancelAllRequests();
  }

  @bind
  getBreadcrumbs(): Breadcrumb[] {
    const { projectId } = this.getNavParams();

    return this.breadcrumbFactory.getProjectBreadcrumbs({
      projectId,
      projectLabel: this.projectBreadcrumb?.getLabel() || projectId,
      nestedTabs: this.getNestedTabs(),
    });
  }

  getNavParams(): ProjectNavParams {
    return { projectId: this.projectBreadcrumb?.getId()?.toString() || this.projectId?.toString() || '' };
  }

  getNestedTabs(): Route[] {
    return UrlHelper.generatePaths(PROJECT_TABS(), this.getNavParams());
  }

  getNavHeader(): ProjectNavHeader {
    return this.projectBreadcrumb?.getLabel();
  }

  render() {
    const loading = this.state.isLoadingNavContext;

    return this.renderBase(
      <SecondaryNav
        title={this.navHeader ?? ''}
        rightSection={
          // FIXME: handle out-of-lifecycle signal updates in tests
          TestValueWrapper.wrap(<ProjectExportDrawer onError={this.handleError} />, null)
        }
        tabs={this.nestedTabs}
        getBreadcrumbs={this.getBreadcrumbs}
        loading={loading}
      >
        <Outlet context={{ project: this.project }} />
      </SecondaryNav>
    );
  }

  private async getData(): Promise<void> {
    try {
      const project = await this.projectRepository.getProject(this.projectId);
      const projectBreadcrumb = this.projectBreadcrumbMapper.mapToBreadcrumb(project);

      this.breadcrumbCachingService.saveProject(projectBreadcrumb);
      this.project = project;
      this.exportState.setState(project);

      this.updateProjectBreadcrumb();
      this.setState({ isLoadingNavContext: false });
    } catch (e: unknown) {
      this.setState({ isLoadingNavContext: false });
      this.handleError(e, { invalidUrl: t('nonExistingProject') });
    }
  }

  private updateProjectBreadcrumb() {
    const cacheBreadcrumb = this.breadcrumbCachingService.getProject(this.projectId);

    // Prevent changes to the object reference of internal
    // properties in case the cache has not changed.
    // This avoids meaningless re-renders on the `SecondaryNav`
    if (isEqual(this.projectBreadcrumb, cacheBreadcrumb)) return;

    this.projectBreadcrumb = cacheBreadcrumb;
    this.navHeader = this.getNavHeader();
    this.nestedTabs = this.getNestedTabs();
  }
}

export const ProjectController = withRouter(Controller);
